/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 */

import { useEffect, useState, useCallback } from 'react';
import axios from '../api/axios';
import {
  DEFAULT_PAGE_SIZE,
  normalizePageSize,
  PAGE_SIZE_OPTIONS,
} from '../config/pagination.config';

export { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS };
const STORAGE_KEY = 'qubepro_userSetting_pageSize';

type UseUserPageSizePreferenceResult = {
  pageSize: number;
  setPageSize: (size: number) => Promise<void>;
  loading: boolean;
  error: string;
};

export function useUserPageSizePreference(defaultSize = DEFAULT_PAGE_SIZE): UseUserPageSizePreferenceResult {
  const [pageSize, setPageSizeState] = useState<number>(() => normalizePageSize(defaultSize));
  const loading = false;
  const [error, setError] = useState<string>('');

  const applyLocalFallback = useCallback(
    (value: any) => {
      const normalized = normalizePageSize(value);
      setPageSizeState(normalized);
      localStorage.setItem(STORAGE_KEY, String(normalized));
    },
    []
  );

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      applyLocalFallback(stored);
    } else {
      applyLocalFallback(defaultSize);
    }

    /** Ağ GET yok: UiPreferencesGate + /auth/me zaten senkronlar; burada yalnızca storage + event */
    const onUserSetting = (e: Event) => {
      const d = (e as CustomEvent<{ key?: string; value?: unknown }>).detail;
      if (d?.key === 'pageSize') {
        applyLocalFallback(d.value);
        setError('');
      }
    };
    window.addEventListener('qubepro-user-setting-changed', onUserSetting as EventListener);

    setError('');

    return () => window.removeEventListener('qubepro-user-setting-changed', onUserSetting as EventListener);
  }, [applyLocalFallback, defaultSize]);

  const setPageSize = useCallback(
    async (size: number) => {
      if (!PAGE_SIZE_OPTIONS.includes(size as (typeof PAGE_SIZE_OPTIONS)[number])) {
        setError('Geçersiz sayfa boyutu');
        return;
      }

      const previous = pageSize;
      setPageSizeState(size);
      localStorage.setItem(STORAGE_KEY, String(size));
      setError('');

      try {
        await axios.patch('/api/settings/user', { pageSize: size });
        try {
          window.dispatchEvent(
            new CustomEvent('qubepro-user-setting-changed', { detail: { key: 'pageSize', value: size } }),
          );
        } catch {
          /* ignore */
        }
      } catch (err: any) {
        console.error('[useUserPageSizePreference] Save error:', err);
        setPageSizeState(previous);
        localStorage.setItem(STORAGE_KEY, String(previous));
        setError(err?.response?.data?.error || 'Sayfa boyutu kaydedilemedi.');
      }
    },
    [pageSize]
  );

  return {
    pageSize,
    setPageSize,
    loading,
    error,
  };
}
